import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import logo from "@/assets/logo_medium.png";
import { supabase } from "@/integrations/supabase/client";
import { loadTurnstileScript, verifyTurnstileToken, getTurnstileSiteKey } from "@/utils/turnstile";

declare global {
  interface Window {
    turnstile?: {
      render: (element: HTMLElement, options: { sitekey: string; size?: string; theme?: "light" | "dark" | "auto"; callback?: (token: string) => void; "error-callback"?: () => void }) => string;
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
      getResponse: (widgetId: string) => string | undefined;
    };
  }
}

const Auth = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [loginData, setLoginData] = useState({ email: "", password: "" });
  const turnstileRef = useRef<HTMLDivElement>(null);
  const turnstileWidgetId = useRef<string | null>(null);
  const turnstileTokenRef = useRef<string | null>(null);

  useEffect(() => {
    const verified = searchParams.get("verified");
    const reset = searchParams.get("reset");
    if (verified === "true") {
      toast.success("E-mail confirmado com sucesso! Você já pode fazer login.");
    }
    if (reset === "true") {
      toast.success("Senha redefinida com sucesso! Faça login com sua nova senha.");
    }

    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (session) {
        const redirectParam = searchParams.get("redirect");
        const safeRedirect = redirectParam && redirectParam.startsWith("/") && !redirectParam.startsWith("//")
          ? decodeURIComponent(redirectParam)
          : null;

        const { data: profile } = await supabase
          .from("profiles")
          .select("health_area_id, onboarding_completed, email_confirmed")
          .eq("id", session.user.id)
          .single();

        if (profile?.onboarding_completed) {
          if (safeRedirect) {
            navigate(safeRedirect);
            return;
          }
          const { data } = await supabase
            .from("user_roles")
            .select("role")
            .eq("user_id", session.user.id);
          const roles = (data ?? []).map((r) => r.role);
          if (roles.includes("admin") || roles.includes("super_admin")) {
            navigate("/admin");
          } else {
            navigate("/app");
          }
          return;
        }

        if (safeRedirect) {
          navigate(safeRedirect);
          return;
        }
        navigate("/onboarding");
      }
    });
  }, [navigate, searchParams]);

  // Carregar script do Turnstile
  useEffect(() => {
    const siteKey = getTurnstileSiteKey();
    console.log("🔍 Turnstile - Site Key:", siteKey ? "✅ Configurada" : "❌ Não configurada");
    
    if (!siteKey) {
      console.warn("⚠️ Turnstile site key não configurada. O widget não será exibido.");
      return;
    }

    const renderWidget = () => {
      if (!turnstileRef.current) {
        console.error("❌ Turnstile - Elemento não encontrado");
        return;
      }

      if (!window.turnstile) {
        console.error("❌ Turnstile - API não disponível");
        return;
      }

      try {
        console.log("🔄 Turnstile - Renderizando widget...");
        const widgetId = window.turnstile.render(turnstileRef.current, {
          sitekey: siteKey,
          size: "normal",
          theme: "light",
          callback: (token: string) => {
            console.log("✅ Turnstile - Token gerado");
            turnstileTokenRef.current = token;
          },
          "error-callback": () => {
            console.error("❌ Turnstile - Erro no widget (error-callback)");
            turnstileTokenRef.current = null;
            toast.error("Erro na verificação de segurança. Verifique se o domínio está configurado corretamente no Cloudflare.");
          },
        });
        turnstileWidgetId.current = widgetId;
        console.log("✅ Turnstile - Widget renderizado com sucesso, ID:", widgetId);
      } catch (error) {
        console.error("❌ Turnstile - Erro ao renderizar:", error);
        const errorMessage = error instanceof Error ? error.message : String(error);
        if (errorMessage.includes("110200")) {
          toast.error("Erro na configuração do Turnstile. Verifique se o domínio está autorizado no Cloudflare.");
        }
      }
    };

    loadTurnstileScript()
      .then(() => {
        console.log("✅ Turnstile - Script carregado");
        // Tentar renderizar imediatamente
        renderWidget();
        
        // Se não funcionou, tentar novamente após um delay
        if (!turnstileWidgetId.current) {
          setTimeout(() => {
            console.log("🔄 Turnstile - Tentando renderizar novamente...");
            renderWidget();
          }, 500);
        }
      })
      .catch((error) => {
        console.error("❌ Erro ao carregar Turnstile:", error);
      });

    return () => {
      if (turnstileWidgetId.current && window.turnstile) {
        window.turnstile.remove(turnstileWidgetId.current);
      }
    };
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();

    setIsLoading(true);

    if (!loginData.email || !loginData.password) {
      toast.error("Preencha todos os campos");
      return;
    }

    // Verificar Turnstile
    const siteKey = getTurnstileSiteKey();
    if (siteKey) {
      if (!turnstileWidgetId.current || !window.turnstile) {
        toast.error("Verificação de segurança não carregada. Recarregue a página.");
        return;
      }

      // Tentar obter token do ref primeiro (mais confiável)
      let turnstileToken = turnstileTokenRef.current;
      
      // Se não há token no ref, tentar obter do widget
      if (!turnstileToken) {
        turnstileToken = window.turnstile.getResponse(turnstileWidgetId.current);
      }
      
      // Se ainda não há token, resetar e aguardar o usuário completar novamente
      if (!turnstileToken) {
        toast.error("Por favor, complete a verificação de segurança");
        // Resetar widget para gerar novo token
        window.turnstile.reset(turnstileWidgetId.current);
        turnstileTokenRef.current = null;
        return;
      }

      // Verificar token no servidor imediatamente (antes que expire)
      const verificationResult = await verifyTurnstileToken(turnstileToken);
      if (!verificationResult.success) {
        // Se o erro for timeout-or-duplicate, resetar e pedir nova verificação
        if (verificationResult.errorCodes?.includes("timeout-or-duplicate")) {
          toast.error("A verificação expirou. Por favor, complete a verificação novamente.");
          // Resetar widget para gerar novo token
          if (turnstileWidgetId.current && window.turnstile) {
            window.turnstile.reset(turnstileWidgetId.current);
            turnstileTokenRef.current = null;
          }
        } else {
          toast.error(verificationResult.error || "Verificação de segurança falhou. Tente novamente.");
          // Resetar widget para permitir nova tentativa
          if (turnstileWidgetId.current && window.turnstile) {
            window.turnstile.reset(turnstileWidgetId.current);
            turnstileTokenRef.current = null;
          }
        }
        return;
      }
      
      // Não limpar/resetar ainda - vamos fazer isso após a requisição (sucesso ou erro)
    }

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: loginData.email,
        password: loginData.password,
      });

      if (error) {
        // Se a requisição falhou, resetar o widget para permitir nova tentativa
        if (siteKey && turnstileWidgetId.current && window.turnstile) {
          turnstileTokenRef.current = null;
          window.turnstile.reset(turnstileWidgetId.current);
        }
        toast.error(error.message);
        setIsLoading(false);
        return;
      }

      if (data.user) {
        // Resetar widget do Turnstile após login bem-sucedido
        if (siteKey && turnstileWidgetId.current && window.turnstile) {
          turnstileTokenRef.current = null;
          window.turnstile.reset(turnstileWidgetId.current);
        }

        const { data: profile } = await supabase
          .from("profiles")
          .select("health_area_id, onboarding_completed, email_confirmed")
          .eq("id", data.user.id)
          .single();

        const redirectParam = searchParams.get("redirect");
        const safeRedirect = redirectParam && redirectParam.startsWith("/") && !redirectParam.startsWith("//")
          ? decodeURIComponent(redirectParam)
          : null;

        if (profile?.onboarding_completed) {
          if (safeRedirect) {
            toast.success("Bem-vindo de volta!");
            navigate(safeRedirect);
            setIsLoading(false);
            return;
          }
          const { data: rolesData } = await supabase
            .from("user_roles")
            .select("role")
            .eq("user_id", data.user.id);
          const roles = (rolesData ?? []).map((r) => r.role);
          toast.success("Bem-vindo de volta!");
          if (roles.includes("admin") || roles.includes("super_admin")) {
            navigate("/admin");
          } else {
            navigate("/app");
          }
          setIsLoading(false);
          return;
        }

        if (safeRedirect) {
          toast.success("Bem-vindo de volta!");
          navigate(safeRedirect);
          setIsLoading(false);
          return;
        }
        toast.success("Bem-vindo! Complete seu cadastro.");
        navigate("/onboarding");
        setIsLoading(false);
        return;
      }
    } catch (error) {
      console.error("Login error:", error);
      toast.error("Erro ao fazer login");
      // Resetar widget do Turnstile em caso de erro
      if (siteKey && turnstileWidgetId.current && window.turnstile) {
        turnstileTokenRef.current = null;
        window.turnstile.reset(turnstileWidgetId.current);
      }
      setIsLoading(false);
    } finally {
      // Garantir que isLoading sempre seja resetado
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-hero flex items-center justify-center p-4">
      <div className="w-full max-w-md animate-fade-in">
        <div className="flex justify-center mb-8">
          <img src={logo} alt="Elinia" className="h-auto w-auto" />
        </div>

        <Card className="shadow-medium">
          <CardContent className="p-8">
            <div className="text-center mb-6">
              <h1 className="text-2xl font-bold">Entrar na sua conta</h1>
              <p className="text-muted-foreground text-sm mt-2">
                Não tem conta?{" "}
                <button onClick={() => navigate("/onboarding")} className="text-primary hover:underline">
                  Criar conta grátis
                </button>
              </p>
            </div>

            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="login-email">E-mail</Label>
                <Input
                  id="login-email"
                  type="email"
                  placeholder="seu@email.com"
                  value={loginData.email}
                  onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="login-password">Senha</Label>
                <Input
                  id="login-password"
                  type="password"
                  placeholder="••••••••"
                  value={loginData.password}
                  onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                  required
                />
              </div>

              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => navigate("/auth/reset-password")}
                  className="text-sm text-primary hover:underline"
                >
                  Esqueceu sua senha?
                </button>
              </div>

              {getTurnstileSiteKey() ? (
                <div className="flex flex-col items-center justify-center py-2 min-h-[65px]">
                  <div 
                    ref={turnstileRef} 
                    id="turnstile-widget"
                    className="w-full flex justify-center"
                    style={{ minHeight: '65px' }}
                  />
                </div>
              ) : (
                <div className="flex justify-center py-2 text-xs text-yellow-600 bg-yellow-50 p-2 rounded">
                  ⚠️ VITE_TURNSTILE_SITE_KEY não configurada no .env
                </div>
              )}

              <Button type="submit" className="w-full" size="lg" disabled={isLoading}>
                {isLoading ? "Entrando..." : "Entrar"}
              </Button>
            </form>
          </CardContent>
        </Card>

        <div className="mt-8 text-center">
          <a href="https://elinia.ai" className="text-sm text-muted-foreground hover:text-primary transition-colors">
            ← Voltar à página principal
          </a>
        </div>
      </div>
    </div>
  );
};

export default Auth;
